import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { css } from '@emotion/react';
import { isEqual } from 'lodash-es';
import { ScheduleAvailabilityTypes as Types } from '@frontend/api-schedule-availability';
import { useDevToolsStore } from '@frontend/devtools';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, CheckboxField, ContentLoader, Text, TimeRangeField } from '@frontend/design-system';
import { AddBreakButton, BreakTimeRangeField, ClosedDayIndicator } from './components';
import { DAYS_OF_WEEK, MAX_WIDTH, ShortWeekDayName, WeekDayName } from './constants';
import {
  convertScheduleToBreaksByDay,
  getScheduleFromAvailabilityHoursFormValues,
  prepareFormValuesFromScheduleInfo,
  removeSecondsFromTimeFormat,
  validateBreak,
} from './helpers';
import { useAvailabilityHoursBreaks, useAvailabilityHoursForm } from './hooks';
import { BreakErrors } from './types';

export type ScheduleAvailabilityHoursValueRef =
  | {
      getUpdatedScheduleInfo: () => Types.ScheduleAvailabilityByDayOfWeek;
      resetAvailabilityHours: () => void;
    }
  | undefined;

interface Props {
  isLoading?: boolean;
  scheduleInfo?: Types.ScheduleAvailabilityByDayOfWeek;
  timeRangeJoiningText?: string;
  closedOfficeText?: string;
  onChange: (hasChanges: boolean, hasError: boolean) => void;
}

const MAX_BREAKS_PER_DAY = 4;

export const ScheduleAvailabilityHours = forwardRef<ScheduleAvailabilityHoursValueRef, Props>(
  ({ isLoading, scheduleInfo, timeRangeJoiningText, closedOfficeText, onChange }, ref) => {
    const { t } = useTranslation('schedule');
    const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
    const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

    const initialBreaksByDay = useMemo(() => convertScheduleToBreaksByDay(scheduleInfo), [scheduleInfo]);
    const form = useAvailabilityHoursForm();
    const {
      breaksByDay,
      addBreak,
      updateBreak,
      deleteBreak,
      clearBreaks,
      resetBreaksAndErrors,
      breakErrors,
      setBreakErrors,
    } = useAvailabilityHoursBreaks({
      initialBreaksByDay,
      validateAvailabilityBreak: validateBreak(form.values),
    });

    // set break errors whenever form values change
    useEffect(() => {
      const newBreakErrors: BreakErrors = DAYS_OF_WEEK.reduce((acc, day) => {
        if (!form.values[`${day}-checkbox`] && breaksByDay[day].length > 0) {
          // clear breaks & errors if checkbox is unchecked
          clearBreaks(day);
          acc[day] = [];
        } else {
          // validate breaks for the selected day
          acc[day] = breaksByDay[day].map((breakTime, index, breaks) =>
            validateBreak(form.values)(day, breaks, breakTime, index)
          );
        }
        return acc;
      }, {} as BreakErrors);

      setBreakErrors(newBreakErrors);
    }, [JSON.stringify(form.values)]);

    const initialScheduleInfo = useMemo<Types.ScheduleAvailabilityByDayOfWeek>(() => {
      if (!scheduleInfo) return {};
      return Object.fromEntries(
        Object.entries(scheduleInfo)
          .filter(([, value]) => Array.isArray(value) && value.length > 0)
          .map(([day, value]) => {
            const { startTime, endTime, breaks } = value[0];
            return [day, [{ startTime, endTime, ...(breaks?.length ? { breaks } : {}) }]];
          })
      );
    }, [scheduleInfo]);

    const newScheduleInfo = useMemo(
      () => getScheduleFromAvailabilityHoursFormValues(form.values, breaksByDay),
      [breaksByDay, form.values]
    );
    const hasChanges = useMemo(
      () => !isEqual(initialScheduleInfo, newScheduleInfo),
      [initialScheduleInfo, newScheduleInfo]
    );
    const hasError = useMemo(
      () => Object.values(breakErrors).some((errors) => errors.some((error) => error)),
      [breakErrors]
    );

    useEffect(() => {
      onChange(hasChanges, hasError);
    }, [onChange, hasChanges, hasError]);

    // set initial values for the form when the component mounts or when initialScheduleInfo changes
    useEffect(() => {
      resetAvailabilityHours();
    }, [initialScheduleInfo]);

    const resetAvailabilityHours = useCallback(() => {
      resetBreaksAndErrors();
      form.seedValues(prepareFormValuesFromScheduleInfo(initialScheduleInfo));
    }, [initialScheduleInfo]);

    useImperativeHandle(ref, () => ({
      getUpdatedScheduleInfo: () => newScheduleInfo,
      resetAvailabilityHours,
    }));

    return (
      <div css={styles.mainContainer}>
        <ContentLoader size='medium' show={isLoading} />
        {DAYS_OF_WEEK.map((day) => (
          <section key={day} css={{ display: 'flex', flexDirection: 'column' }}>
            <div css={styles.timeRangeFieldRowContainer}>
              <CheckboxField
                label={isMobile ? ShortWeekDayName[day] : WeekDayName[day]}
                className='day-checkbox'
                {...form.getFieldProps(`${day}-checkbox`)}
              />
              {form.values[`${day}-checkbox`] ? (
                <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
                  {/* Note: keep this extra div to correctly align error messages with the time range field */}
                  <div css={{ p: { margin: 0 } }}>
                    <TimeRangeField
                      startLabel=''
                      endLabel=''
                      label={t('{{day}} Time Range', { day: WeekDayName[day] })}
                      joiningText={timeRangeJoiningText || t('to')}
                      {...form.getFieldProps(`${day}-time-range`)}
                      css={styles.timeRangeField}
                    />
                  </div>
                  <AddBreakButton
                    day={day}
                    onClick={addBreak}
                    disabled={breaksByDay[day].length >= MAX_BREAKS_PER_DAY}
                  />
                </div>
              ) : (
                <ClosedDayIndicator text={closedOfficeText} />
              )}
            </div>

            {breaksByDay[day].map((breakTime, index) => {
              const weekHours = form.values[`${day}-time-range`];
              const minValue = weekHours?.[0] ? removeSecondsFromTimeFormat(weekHours[0]) : undefined;
              const maxValue = weekHours?.[1] ? removeSecondsFromTimeFormat(weekHours[1]) : undefined;

              return (
                <div
                  key={`${breakTime[0]}-${breakTime[1]}-${index}`}
                  css={[styles.timeRangeFieldRowContainer, { backgroundColor: theme.colors.neutral5 }]}
                >
                  <Text className='break-label'>{t('Break')}</Text>
                  <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
                    {/* Note: keep this extra div to correctly align error messages with the time range field */}
                    <div css={{ p: { margin: 0 } }}>
                      <BreakTimeRangeField
                        day={day}
                        index={index}
                        joiningText={timeRangeJoiningText}
                        value={breakTime}
                        onChange={(value) => updateBreak(day, index, value)}
                        minTime={minValue}
                        maxTime={maxValue}
                        errorMessage={breakErrors[day]?.[index]}
                      />
                    </div>
                    <Button
                      iconName='trash-small'
                      variant='secondary'
                      hoverLabel={t('Delete Break')}
                      css={styles.trashIcon}
                      onClick={() => deleteBreak(day, index)}
                    />
                  </div>
                </div>
              );
            })}
          </section>
        ))}
        {isDebugModeOn && (
          <div css={{ display: 'flex', background: theme.colors.primary5, padding: theme.spacing(2) }}>
            <div>
              form.values:
              <pre>{JSON.stringify(form.values, null, 1)}</pre>
            </div>
            <div>
              breaksByDay:
              <pre>{JSON.stringify(breaksByDay, null, 1)}</pre>
            </div>
            <div>
              breakErrors:
              <pre>{JSON.stringify(breakErrors, null, 1)}</pre>
            </div>
            <div>
              newScheduleInfo:
              <pre>{JSON.stringify(newScheduleInfo, null, 1)}</pre>
            </div>
            <div>
              initialScheduleInfo:
              <pre>{JSON.stringify(initialScheduleInfo, null, 1)}</pre>
            </div>
          </div>
        )}
      </div>
    );
  }
);

ScheduleAvailabilityHours.displayName = 'ScheduleAvailabilityHours';

const styles = {
  mainContainer: css({
    maxWidth: MAX_WIDTH,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflowX: 'auto',
  }),

  timeRangeFieldRowContainer: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    minHeight: 60,
    '.day-checkbox': {
      gap: theme.spacing(1),
    },
    '.break-label': {
      marginLeft: theme.spacing(8),
    },
    '@container (max-width: 700px)': {
      minWidth: 'min(375px, 100%)',
      padding: theme.spacing(1, 1),
      '.day-checkbox': {
        gap: theme.spacing(0.5),
      },
      '.break-label': {
        marginLeft: theme.spacing(4),
      },
      '.start-time, .end-time': {
        width: 90,
        input: {
          padding: theme.spacing(0, 1.5),
        },
      },
    },
  }),
  timeRangeField: css({
    gap: theme.spacing(1),
    input: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.small,
    },
    '@container (max-width: 700px)': {
      gap: theme.spacing(0.5),
    },
  }),
  trashIcon: css({
    height: theme.spacing(2.5),
    width: theme.spacing(2.5),
    padding: 0,
  }),
};
